import { gql, useMutation } from '@apollo/client';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import React from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/coreComponents/button';
import { DrawerModal, DrawerModalButtonsWrapper } from '../components/coreComponents/drawerModal';
import { Input } from '../components/coreComponents/input';
import { SelectInput, SelectOption } from '../components/coreComponents/selectInput';
import { AdminRole } from '../graphql/types';
import { useIdentity } from '../identityContext';

interface InviteAdminDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  role: AdminRole;
}

// Invite drawer. Creates a DRAFT admin (no approval) and redirects to their
// overview to finish editing + request credentials. Role options are gated by
// the inviter: an admin can only invite admins; a superAdmin can invite either.
export function InviteAdminDrawer(props: InviteAdminDrawerProps) {
  const identity = useIdentity();
  const navigate = useNavigate();
  const [invite] = useMutation(inviteMutation);

  const roleOptions: ReadonlyArray<SelectOption<AdminRole>> =
    identity.role === 'superAdmin'
      ? [
          { label: 'Admin', value: 'admin' },
          { label: 'Superadmin', value: 'superAdmin' }
        ]
      : [{ label: 'Admin', value: 'admin' }];

  const formik = useFormik<FormValues>({
    initialValues: { firstName: '', lastName: '', email: '', role: 'admin' },
    validate: values => {
      const errors: Partial<Record<keyof FormValues, string>> = {};
      if (!values.firstName.trim()) errors.firstName = 'First name is required';
      if (!values.lastName.trim()) errors.lastName = 'Last name is required';
      if (!values.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
        errors.email = 'Enter a valid email address';
      }
      if (!values.role) errors.role = 'Role is required';
      return errors;
    },
    onSubmit: async (values, helpers) => {
      try {
        const response = await invite({
          variables: {
            input: {
              firstName: values.firstName.trim(),
              lastName: values.lastName.trim(),
              email: values.email.trim().toLowerCase(),
              role: values.role
            }
          },
          refetchQueries: ['AdminUserMany']
        });
        const created = response.data?.adminInvite;
        if (created == null) throw new Error('No response from server.');
        handleClose();
        navigate(`/administrators/${created.id}/overview`);
      } catch (err: any) {
        helpers.setStatus(err.message ?? 'Failed to invite administrator');
      }
    }
  });

  const handleClose = () => {
    formik.resetForm();
    props.onClose();
  };

  return (
    <DrawerModal open={props.open} onClose={handleClose} title="Invite administrator">
      <Box sx={{ px: 8, flex: 1 }}>
        <Typography variant="labelSmall" sx={{ color: 'text.secondary', mb: 4, display: 'block' }}>
          Creates a draft administrator. They won't be able to sign in until you request login
          credentials from their profile and a second administrator confirms it.
        </Typography>

        <Stack spacing={5}>
          <Input
            label="First name"
            name="firstName"
            placeholder="John"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.firstName && formik.errors.firstName
                ? formik.errors.firstName
                : undefined
            }
            disabled={formik.isSubmitting}
          />
          <Input
            label="Last name"
            name="lastName"
            placeholder="Doe"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.lastName && formik.errors.lastName ? formik.errors.lastName : undefined
            }
            disabled={formik.isSubmitting}
          />
          <Input
            label="Email"
            name="email"
            placeholder="admin@example.com"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && formik.errors.email ? formik.errors.email : undefined}
            disabled={formik.isSubmitting}
          />
          <SelectInput
            label="Role"
            placeholder="Select a role"
            value={formik.values.role}
            onChange={value => formik.setFieldValue('role', value ?? '')}
            options={roleOptions}
            error={formik.touched.role && Boolean(formik.errors.role)}
            helperText={formik.touched.role ? formik.errors.role : undefined}
            disabled={formik.isSubmitting}
          />

          {formik.status != null && <Alert severity="error">{formik.status}</Alert>}
        </Stack>
      </Box>

      <DrawerModalButtonsWrapper>
        <Button
          variant="primary"
          onClick={() => formik.handleSubmit()}
          disabled={formik.isSubmitting}
        >
          {formik.isSubmitting ? 'Inviting…' : 'Invite administrator'}
        </Button>
        <Button variant="secondary" onClick={handleClose} disabled={formik.isSubmitting}>
          Cancel
        </Button>
      </DrawerModalButtonsWrapper>
    </DrawerModal>
  );
}

const inviteMutation = gql<AdminInviteGQL>`
  mutation AdminInvite($input: AdminInviteInput!) {
    adminInvite(input: $input) {
      id
    }
  }
`;
